import { useQuery, useQueryClient } from '@tanstack/react-query'
import { COLLECTION_KEYS, SEALED_COLLECTION_KEYS } from '@/hooks/collections/collectionKeys'
import { BUYLIST_KEYS } from '@/hooks/lists/useBuylist'
import { SELLLIST_KEYS } from '@/hooks/lists/useSelllist'
import { api } from '@/services/api'
import { useAuthStore } from '@/store/authStore'
import type { UserQuantities } from '@/types'

const USER_QUANTITIES_KEY = () => ['userQuantities'] as const

/**
 * Fetches all user quantities (collection, buylist, selllist) in a single request.
 * Seeds the individual quantity caches so existing hooks find their data pre-populated.
 */
export function useUserQuantities() {
  const queryClient = useQueryClient()
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated)

  return useQuery<UserQuantities>({
    queryKey: USER_QUANTITIES_KEY(),
    queryFn: async () => {
      const data = await api.getUserQuantities()
      // Seed individual caches so hooks using them don't fire their own requests
      queryClient.setQueryData(COLLECTION_KEYS.quantities(), data.collection)
      queryClient.setQueryData(BUYLIST_KEYS.quantities(), data.buylist)
      queryClient.setQueryData(SELLLIST_KEYS.quantities(), data.selllist)
      queryClient.setQueryData(SEALED_COLLECTION_KEYS.quantities(), data.sealedCollection)
      return data
    },
    staleTime: 5 * 60 * 1000,
    enabled: isAuthenticated,
  })
}
